import React, { useState, useCallback } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    FileInput,
    FileField,
    SelectInput,
    CheckboxGroupInput,
    required,
    useRedirect,
} from "react-admin";
import { Backdrop, LinearProgress, Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';

const httpClient = (url, formData, onProgress) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem("token")}`);
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = Math.round((event.loaded / event.total) * 100);
                onProgress(percentComplete);
            }
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(JSON.parse(xhr.responseText));
            } else {
                try {
                    const response = JSON.parse(xhr.responseText);
                    reject({ status: xhr.status, body: response });
                } catch {
                    reject(new Error(xhr.statusText || 'Unknown error'));
                }
            }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
    });
};

const validateFileTypeAndSize = (file) => {
    if (!file || !file.rawFile) return undefined;

    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];
    if (!validTypes.includes(file.rawFile.type)) {
        return 'Only MP3, WAV, or OGG files are allowed.';
    }

    // Validate file size (100MB = 100 * 1024 * 1024 bytes)
    const maxSizeInBytes = 100 * 1024 * 1024;
    if (file.rawFile.size > maxSizeInBytes) {
        return 'File size must not exceed 100MB.';
    }

    return undefined;
};

export const AudioCreate = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const redirect = useRedirect();

    const customSave = useCallback(async (data) => {
        // Basic validations
        if (!data.file || !data.file.rawFile) {
            toast.warn("Please select an audio file.");
            return;
        }
        if (!data.target_lang || data.target_lang.length === 0) {
            toast.warn("Please select at least one target language.");
            return;
        }

        setIsLoading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('file', data.file.rawFile);
        formData.append('src_lang', data.src_lang);
        data.target_lang.forEach(lang => formData.append('target_lang[]', lang.trim()));

        try {
            await httpClient(`${process.env.REACT_APP_API_ENTRYPOINT}/audio`, formData, setUploadProgress);
            toast.success("Audio created successfully.");
            redirect('/audio');
        } catch (error) {
            setIsLoading(false);
            console.error("Upload Error:", error);

            // Handle violations from the server using toast directly
            if (error.body && error.body.violations && Array.isArray(error.body.violations)) {
                error.body.violations.forEach(violation => {
                    toast.error(violation.message || 'An error occurred');
                });
            } else {
                toast.error(`Error: ${error.message || "An unexpected error occurred."}`);
            }
        }
    }, [redirect]);

    return (
        <>
            <Create>
                <SimpleForm onSubmit={customSave}>
                    <div className="file-input-container">
                        <TextInput source="title" label="Title" validate={required()} fullWidth />
                        <FileInput
                            className="file-input"
                            source="file"
                            label="Audio File"
                            accept="audio/mpeg,audio/wav,audio/ogg"
                            validate={[required(), validateFileTypeAndSize]}
                            placeholder={<p>Drop your MP3 Or WAV file here or click to select</p>}
                            fullWidth
                        >
                            <FileField source="src" title="title" />
                        </FileInput>
                    </div>
                    <div className="file-input-container">
                        <SelectInput
                            source="src_lang"
                            choices={[
                                { id: 'ar', name: 'Arabic' },
                                { id: 'en', name: 'English' },
                            ]}
                            validate={required()}
                            label="Source Language"
                            fullWidth
                        />
                        <CheckboxGroupInput
                            source="target_lang"
                            label="Target Languages"
                            choices={[
                                { id: 'ar', name: 'Arabic' },
                                { id: 'en', name: 'English' },
                            ]}
                            validate={required()}
                        />
                    </div>
                </SimpleForm>
            </Create>
            <Backdrop
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    color: '#fff',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                open={isLoading}
            >
                <Box sx={{ width: '50%', maxWidth: 400 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography sx={{ mt: 2, fontSize: '1.2rem' }}>
                        Uploading Audio: {uploadProgress}%
                    </Typography>
                </Box>
            </Backdrop>
        </>
    );
};